import {
  alpha,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditNoteIcon from "@mui/icons-material/EditNote";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect } from "react";

import { warmTheme } from "../OrdenServicioTraslado/OrdenServicioTraslado";
import {
  CustomTextArea,
  CustomTextFieldNoForm,
  CustomTextFieldNoFormWithName,
} from "@/shared/components/ui";
import { gridSizeMdLg12, gridSizeMdLg6 } from "@/shared";
import { CacheKeyCalendarioENUM, Contrato, Sector } from "@/shared/interfaces";
import useSelectDateTimerPreScheduling, { DepartamentoSelectScheduling } from "@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store";
import { SelectDateTimerSchedulingPyl } from "@/erp/operaciones/agendamiento/shared/components/calendario-preagendamiento-pyl/SelectDateTimerSchedulingPyl";
import OtrosConceptosVisita from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";
import { usePreciosConceptosVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita";
import TipoInstalacionSelector from "@/erp/orden-servicio/orden-servicio-visita/components/tipo-instalacion/TipoInstalacionSelector";
import { useTiposInstalacion } from "@/erp/orden-servicio/orden-servicio-visita/components/tipo-instalacion/useTiposInstalacion";

type VisitaFormProps = {
  contrato: Contrato | null;
  sector?: Sector | null;
  form: any;
};

const toText = (value: any) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const toSiNo = (value: any) => {
  if (value === true) return "SI";
  if (value === false) return "NO";
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

export default function VisitaForm({ contrato, sector, form }: VisitaFormProps) {
  const {
    control,
    watch,
    formState: { errors },
  } = form;
  const contratoAny = contrato as any;
  const { conceptos: catalogoConceptos, metodosPago } = usePreciosConceptosVisita();
  const { tipos: tiposInstalacion } = useTiposInstalacion();

  useEffect(() => {
    if (!contrato) {
      form.reset({
        ...form.getValues(),
        pais: "",
        provincia: "",
        ciudad: "",
        parroquia: "",
        zona: "",
        direccion: "",
        observaciones: "",
        fecha_visita: ""

      })

    }
  }, [contrato])

  const getFloat = useSelectDateTimerPreScheduling(s => s.getFloat)
  const setConfigParams = useSelectDateTimerPreScheduling(s => s.setConfigParams);
  const setPreventa = useSelectDateTimerPreScheduling(s => s.setPreventa)

  useEffect(() => {
    setConfigParams({
      time: 10 * 60 * 1000,
      tiempo: CacheKeyCalendarioENUM.TIEMPO_LIMITE_PYL,
      motivo: CacheKeyCalendarioENUM.MENSAJE_BLOQUEO_PYL,
      departamento: DepartamentoSelectScheduling.PYL,
    });

    const zonaFlota = sector?.zona || contrato?.zona;
    console.log('zona para filtrar flotas:', zonaFlota);
    if (zonaFlota) {
      getFloat({
        estado: true,
        zonas: zonaFlota,
        page_size: 100
      })
    }
    if (contrato?.id_contrato) {
      setPreventa(contrato.id_contrato)
    }
  }, []);

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ my: 2 }}>
        <Chip
          label="Información de Visita"
          size="small"
          sx={{
            bgcolor: alpha(warmTheme.palette.primary.main, 0.1),
            color: "primary.dark",
            fontWeight: 600,
          }}
        />
      </Divider>

      {/* DATOS CLIENTE */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(warmTheme.palette.secondary.main, 0.15),
          borderRadius: 2,
          mb: 3,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.secondary.main, 0.3),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 2 }}>
          DATOS CLIENTE
        </Typography>

        <Grid container spacing={3}>
          <CustomTextFieldNoForm
            label="CI / RUC"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.identificacion ?? contratoAny?.identificacion)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Nombre"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.razon_social ?? contratoAny?.razon_social ?? "N/A")}
            size={gridSizeMdLg6}
          />
        </Grid>
      </Paper>

      {/* DATOS COMPLEMENTARIOS - SOLO LECTURA */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(warmTheme.palette.primary.main, 0.05),
          borderRadius: 2,
          mb: 3,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.primary.main, 0.2),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 2 }}>
          DATOS COMPLEMENTARIOS
        </Typography>

        <Grid container spacing={3}>
          <CustomTextFieldNoForm
            label="Fecha nacimiento"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.fecha_nacimiento ?? contratoAny?.fecha_nacimiento)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Edad"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.edad ?? contratoAny?.edad)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Tercera edad"
            required={false}
            disabled={true}
            defaultValue={toSiNo(contrato?.cliente_data?.es_tercera_edad ?? contratoAny?.es_tercera_edad)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Tutor / Curador"
            required={false}
            disabled={true}
            defaultValue={toSiNo(contrato?.cliente_data?.es_curador_tutor ?? contratoAny?.es_curador_tutor)}
            size={gridSizeMdLg6}
          />

          <CustomTextFieldNoForm
            label="Línea"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.lineas ?? contratoAny?.lineas ?? contratoAny?.linea)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Aplica bono desarrollo humano"
            required={false}
            disabled={true}
            defaultValue={toSiNo(contrato?.cliente_data?.es_bono_humano ?? contratoAny?.es_bono_humano)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Discapacidad"
            required={false}
            disabled={true}
            defaultValue={toSiNo(contrato?.cliente_data?.es_discapacitado ?? contratoAny?.es_discapacitado)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Aplica ayuda social"
            required={false}
            disabled={true}
            defaultValue={toSiNo(contrato?.cliente_data?.es_ayuda_social ?? contratoAny?.es_ayuda_social)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Aplica gremio"
            required={false}
            disabled={true}
            defaultValue={toSiNo(contrato?.plan_data?.es_gremio ?? contratoAny?.plan_data?.es_gremio)}
            size={gridSizeMdLg6} />
          <CustomTextFieldNoForm
            label="Teléfono adicional"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.celular_2 ?? contratoAny?.celular)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoForm
            label="Email"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.email ?? contratoAny?.email)}
            size={gridSizeMdLg6} />
          <CustomTextFieldNoForm
            label="Número Referencia"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.cliente_data?.numero_referencia)}
            size={gridSizeMdLg6} />
          <CustomTextFieldNoForm
            label="Parentesco referencia"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.persona_referencia_parentesco || contrato?.persona_referencia_nombre)}
            size={gridSizeMdLg6} />
          <CustomTextFieldNoForm
            label="Celular referencia"
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.persona_referencia_celular || contrato?.persona_referencia_celular_2)}
            size={gridSizeMdLg6}
          />
        </Grid>
      </Paper>

      {/* UBICACION SERVICIO - SOLO LECTURA */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          bgcolor: alpha(warmTheme.palette.primary.main, 0.05),
          borderRadius: 2,
          mb: 3,
          border: "1px solid",
          borderColor: alpha(warmTheme.palette.primary.main, 0.2),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ mb: 2 }}>
          UBICACION SERVICIO
        </Typography>

        <Grid container spacing={3}>
          <CustomTextFieldNoFormWithName
            label="País"
            name="pais"
            control={control}
            required={false}
            disabled={true}
            defaultValue={toText(sector?.pais_data?.nombre ?? contrato?.pais)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoFormWithName
            label="Provincia"
            name="provincia"
            control={control}
            required={true}
            disabled={true}
            defaultValue={toText(sector?.provincia_data?.nombre ?? contrato?.provincia)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoFormWithName
            label="Ciudad"
            name="ciudad"
            control={control}
            required={false}
            disabled={true}
            defaultValue={toText(sector?.ciudad_data?.nombre ?? contrato?.ciudad)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoFormWithName
            label="Parroquia"
            name="parroquia"
            control={control}
            required={false}
            disabled={true}
            defaultValue={toText(sector?.nombre ?? contrato?.parroquia)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoFormWithName
            label="Zona"
            name="zona"
            control={control}
            required={false}
            disabled={true}
            defaultValue={toText(sector?.zona_data?.nombre ?? contrato?.zona)}
            size={gridSizeMdLg6}
          />

          <CustomTextFieldNoFormWithName
            label="Dirección"
            name="direccion"
            control={control}
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.direccion)}
            size={gridSizeMdLg6}
          />
          <CustomTextFieldNoFormWithName
            label="Coordenadas Actual"
            name="coordenadas"
            control={control}
            required={false}
            disabled={true}
            defaultValue={toText(contrato?.coordenadas)}
            size={gridSizeMdLg12}
          />
        </Grid>
      </Paper>

      {/* DATOS VISITA - EDITABLE */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "2px solid",
          borderColor: alpha(warmTheme.palette.primary.main, 0.4),
          bgcolor: alpha(warmTheme.palette.primary.main, 0.03),
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            bgcolor: "primary.main",
            borderRadius: "2px 2px 0 0",
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} color="primary.dark">
              CONFIRMACIÓN DE LA VISITA EN SITIO
            </Typography>
          </Stack>
          <Tooltip title="Complete la fecha programada y las observaciones relevantes para el técnico que realizará la visita." arrow>
            <InfoOutlinedIcon sx={{ color: "text.secondary", fontSize: 18, cursor: "pointer" }} />
          </Tooltip>
        </Stack>

        <Divider sx={{ mb: 2.5, borderStyle: "dashed" }} />

        <Grid container spacing={3}>

          <Grid item xs={12}>
            <SelectDateTimerSchedulingPyl />
          </Grid>

          {/* Observaciones */}
          <Grid item xs={12}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <EditNoteIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Observaciones para el técnico
                </Typography>
              </Stack>
              <Grid pt={2}>
                <CustomTextArea
                  control={control}
                  name="observaciones"
                  label="Describe detalles de acceso, equipos o instrucciones especiales (opcional)"
                  required={false}
                  typeOfEntry="observacion"
                  rows={3}
                  size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                  error={errors.observaciones}
                  helperText={errors.observaciones?.message}
                  defaultValue={watch("observaciones") || ""}
                />

              </Grid>

            </Stack>
          </Grid>

          {/* Tipo de instalación (árbol) */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 2, borderStyle: "dashed" }} />
            <TipoInstalacionSelector
              value={watch("tipo_instalacion") || null}
              onChange={(v) => form.setValue("tipo_instalacion", v, { shouldDirty: true })}
              tipos={tiposInstalacion}
            />
          </Grid>

          {/* Otros conceptos (equipos + método de pago) → cobro Softnet */}
          <Grid item xs={12}>
            <Divider sx={{ mb: 2, borderStyle: "dashed" }} />
            <OtrosConceptosVisita
              value={watch("otros_conceptos") || []}
              onChange={(v) => form.setValue("otros_conceptos", v, { shouldDirty: true })}
              conceptos={catalogoConceptos}
              metodosPago={metodosPago}
            />
          </Grid>

        </Grid>
      </Paper>
    </Box>
  );
}